"use client";

import { CSSProperties, FC } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { ProgramStructure2Data } from "./_schema";
import { ArrowDownIcon } from "./_arrow";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { shadowFromColor } from "@/utils/utils";

/**
 * ProgramStructure2 — Variant 2
 * Light / maroon themed syllabus design from Figma "Typography System · 4 Breakpoints".
 * Responsive across the design system's five steps: base (mobile, <1080), then the project's
 * named breakpoints sm (1080), md (1230), lg (1430), xl (1750) — each Figma frame's font sizes,
 * spacing and column widths are matched exactly.
 *
 * The data model (semesters, categories, subjects, electives, tools, skills) is identical to
 * Variant 1; only the presentation differs. Colors fall back to the exact Figma values when
 * the CMS still holds Variant-1 oriented defaults, while any color the editor intentionally
 * overrides is respected.
 */

// ---- Figma design tokens ----
const MAROON = "#9f1c34";
const INK = "#101112";
const ROW_DEFAULT_BG = "rgba(0,0,0,0.02)";
const ROW_DIVIDER = "rgba(0,0,0,0.1)";
const HEADER_DIVIDER = "rgba(255,255,255,0.35)";
const SKILL_PILL_BG = "rgba(0,0,0,0.1)";
const DROPDOWN_BG = "rgba(0,0,0,0.03)";
const DROPDOWN_BORDER = "rgba(0,0,0,0.4)";
// CTA drop shadow — identical to Program Fee 3's button.
const CTA_SHADOW = "rgba(202,125,139,0.6)";
const CTA_SHADOW_ALPHA = 0.6;
const CTA_SHADOW_OFFSET = "0px 10px 10px";
const TOOL_PILL_GRADIENT =
    "radial-gradient(ellipse at 35% 40%, rgba(57,10,19,0) 0%, rgba(108,19,35,0.05) 50%, rgba(159,28,52,0.10) 100%)";

const FONT_POPPINS = "var(--font-poppins)";
const FONT_VISUAL = "var(--font-wf-visual-sans)";

// Recurring responsive scales (shared so header/rows/total stay perfectly aligned)
const CELL = "px-[16px] py-[10px] sm:px-[24px] md:py-[12px] lg:py-[16px] gap-[8px] sm:gap-[20px]";
// Below `sm` the columns are wide enough to hold a word rather than squeezing it —
// the table scrolls horizontally instead of letting text spill into the next column.
const TYPE_W = "w-[100px] sm:w-[120px]";
const CREDITS_W = "w-[80px] sm:w-[160px]";
// type + credits + dividers + row padding + gaps, leaving ~200px for the subject name.
const TABLE_MIN_W = "min-w-[450px] sm:min-w-0";
const ROW_TEXT =
    "text-[12px] sm:text-[14px] lg:text-[15px] xl:text-[16px] leading-[1.55] tracking-[0.06px] sm:tracking-[0.07px] lg:tracking-[0.075px] xl:tracking-[0.08px]";

// Returns `value` when the editor set a real override, otherwise the Figma v2 default.
// Treats empty values and known Variant-1 defaults as "unset".
const themed = (value: string | undefined | null, v1Defaults: string[], v2Default: string): string => {
    if (!value) return v2Default;
    return v1Defaults.includes(value) ? v2Default : value;
};

// Background: respects the same bg-variant system as Variant 1, but defaults to a clean
// solid white (Figma) instead of the Variant-1 teal gradient when nothing was configured.
const buildV2Background = (d: ProgramStructure2Data, isMobile: boolean): CSSProperties => {
    const solid = themed(d.background_color, ["#000000", ""], "#FFFFFF");
    const gc1 = d.gradient_color_1;
    const usingDefaultGradient = !gc1 || gc1 === "#00D4EB" || gc1 === "rgba(0, 212, 235, 0.33)";
    if ((d.backvariant === "variant_1" || d.backvariant === "variant_2") && !usingDefaultGradient) {
        return formatBackgroundStyle(d, isMobile, solid);
    }
    return { backgroundColor: solid };
};

interface Variant2Props {
    parsedData: ProgramStructure2Data;
    semesters: any[];
    activeSemester: number;
    onSemesterChange: (index: number) => void;
    activeCategories: any[];
    selectedItemsByCategory: Record<number, number>;
    onCategoryItemSelect: (categoryId: number, itemId: number) => void;
    openDropdown: number | null;
    onDropdownToggle: (categoryId: number | null) => void;
    activeSubjects: any[];
    totalCredits: number;
    expandedElective: number | null;
    onElectiveToggle: (id: number | null) => void;
    onCtaClick: () => void;
    isMobile: boolean;
    isAnimationEnabled: boolean;
}

// Thin vertical divider between table columns. Alignment is inherited from the row's
// align-items (center for normal rows, start for elective rows) so no self-* is needed.
const Divider: FC<{ color: string }> = ({ color }) => (
    <div className="shrink-0 w-px h-[22px]" style={{ backgroundColor: color }} />
);

export const ProgramStructure2Variant2: FC<Variant2Props> = ({
    parsedData,
    semesters,
    activeSemester,
    onSemesterChange,
    activeCategories,
    selectedItemsByCategory,
    onCategoryItemSelect,
    openDropdown,
    onDropdownToggle,
    activeSubjects,
    totalCredits,
    expandedElective,
    onElectiveToggle,
    onCtaClick,
    isMobile,
}) => {
    const titleColor = themed(parsedData.title_1_color, ["#CCCCCC", "rgba(255,255,255,0.8)"], INK);
    const descColor = themed(parsedData.description_color, ["#B3B3B3", "rgba(255,255,255,0.7)"], INK);
    const creditsLabelColor = themed(parsedData.credits_text_color, ["#FFFFFF"], INK);
    const creditsNumberColor = themed(parsedData.total_credits_color, ["#0febff"], MAROON);
    const headerBg = parsedData.table_header_background_color || MAROON;
    const totalTextColor = themed(parsedData.table_total_text_color, ["#FFFFFF"], INK);
    const noteColor = themed(parsedData.note_color, ["#FFFFFF"], INK);
    const toolsTitleColor = themed(parsedData.tools_title_color, ["#4cecfa"], INK);
    const skillsTitleColor = themed(parsedData.skills_title_color, ["#4cecfa"], INK);
    const ctaBg = themed(parsedData.cta_button_background_color, ["#FFFFFF"], MAROON);
    const ctaTextColor = themed(parsedData.cta_button_text_color, ["#222222"], "#FFFFFF");
    const shadowEnabled = parsedData.cta_button_shadow_enabled === true || parsedData.cta_button_shadow_enabled === 1;
    // Same soft glow as Program Fee 3: `0px 10px 10px rgba(202,125,139,0.6)`.
    // The CMS field is a native <input type="color">, so anything the editor
    // picks comes back as an OPAQUE hex — dropping it straight into box-shadow
    // painted a hard slab under the button instead of a shadow. Re-applying the
    // 0.6 alpha keeps the picker useful and the result translucent either way.
    const shadowColor = shadowFromColor(
        parsedData.cta_button_shadow_color || CTA_SHADOW,
        CTA_SHADOW_ALPHA,
    );

    const bgStyle = buildV2Background(parsedData, isMobile);
    const totalCreditsText = (semesters[activeSemester] as any)?.total_credits_text || parsedData.table_total_text;

    return (
        <section
            className={cn(
                "relative w-full overflow-hidden flex justify-center",
                "px-[24px] py-[40px]",
                "sm:px-[40px] sm:py-[60px]",
                "md:px-[60px]",
                "lg:px-[80px]",
                "xl:px-[160px]"
            )}
            style={bgStyle}
        >
            <div className="relative z-10 flex flex-col w-full min-w-0 max-w-[1430px] gap-[24px] sm:gap-[20px] lg:gap-[40px]">
                {/* Header block: title/description, credits + tabs, dropdowns */}
                <div className="flex flex-col w-full min-w-0 gap-[24px] sm:gap-[40px]">
                    {/* Title + description */}
                    <div className="flex flex-col gap-[16px] sm:gap-[23px] w-full">
                        <p
                            className={cn(
                                "text-center font-semibold w-full [word-break:break-word]",
                                "text-[28px] leading-[1.25] tracking-[-0.42px]",
                                "md:text-[32px] md:tracking-[-0.48px]",
                                "lg:text-[36px] lg:tracking-[-0.54px]",
                                "xl:text-[42px] xl:tracking-[-0.63px] xl:leading-[1.2]"
                            )}
                            style={{ color: titleColor, fontFamily: FONT_POPPINS }}
                        >
                            {parsedData.title_1}
                            {parsedData.title_2}
                        </p>
                        {parsedData.description && (
                            <p
                                className={cn(
                                    "text-center w-full [word-break:break-word] font-medium sm:font-normal leading-[1.65]",
                                    "text-[14px] sm:text-[16px] lg:text-[17px] xl:text-[18px]"
                                )}
                                style={{ color: descColor, fontFamily: FONT_POPPINS }}
                            >
                                {parsedData.description}
                            </p>
                        )}
                    </div>

                    {/* Total credits + semester tabs */}
                    <div className="flex flex-col gap-[24px] w-full min-w-0 sm:flex-row sm:items-center sm:justify-between">
                        {parsedData.credits_text && parsedData.total_credits && (
                            <p
                                className={cn(
                                    "order-1 sm:order-2 font-semibold whitespace-nowrap shrink-0 leading-[1.3]",
                                    "text-[20px] tracking-[-0.2px]",
                                    "sm:text-[24px] sm:tracking-[-0.24px]",
                                    "lg:text-[28px] lg:tracking-[-0.28px]",
                                    "xl:text-[32px] xl:tracking-[-0.32px]"
                                )}
                                style={{ fontFamily: FONT_POPPINS }}
                            >
                                <span style={{ color: creditsLabelColor }}>{parsedData.credits_text} </span>
                                <span style={{ color: creditsNumberColor }}>{parsedData.total_credits}</span>
                            </p>
                        )}

                        <div className="order-2 sm:order-1 flex gap-[10px] sm:gap-[12px] overflow-x-auto scrollbar-hide sm:overflow-visible min-w-0">
                            {semesters.map((semester, index) => {
                                const isActive = activeSemester === index;
                                const activeBg = themed(semester.active_color || parsedData.semester_active_bg_color, ["#02a7b6"], MAROON);
                                const activeText = semester.active_text_color || parsedData.semester_active_text_color || "#FFFFFF";
                                const inactiveBorder = themed(
                                    semester.text_color || parsedData.semester_inactive_border_color,
                                    ["#FFFFFF", "rgba(255,255,255,0.4)"],
                                    DROPDOWN_BORDER
                                );
                                const inactiveText = themed(semester.text_color || parsedData.semester_inactive_text_color, ["#FFFFFF"], INK);
                                return (
                                    <button
                                        key={semester.id || index}
                                        data-cta-name="semester_tab"
                                        onClick={() => onSemesterChange(index)}
                                        className={cn(
                                            "shrink-0 flex items-center justify-center whitespace-nowrap transition-all px-[16px] py-[10px]",
                                            "text-[14px] lg:text-[15px] xl:text-[18px]",
                                            isActive ? "font-semibold rounded-[40px]" : "font-normal rounded-[70px]"
                                        )}
                                        style={{
                                            fontFamily: FONT_POPPINS,
                                            ...(isActive
                                                ? { backgroundColor: activeBg, color: activeText, border: `1px solid ${activeBg}` }
                                                : { backgroundColor: "transparent", color: inactiveText, border: `0.6px solid ${inactiveBorder}` }),
                                        }}
                                    >
                                        {semester.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Category dropdowns */}
                    {activeCategories.length > 0 && (
                        <div className="flex flex-col gap-[10px] w-full min-w-0 sm:flex-row sm:w-[480px]">
                            {activeCategories.map((category) => {
                                const labelColor = themed(category.text_color, ["#FFFFFF"], INK);
                                const selectedName =
                                    category.items?.find((item: any) => item.id === selectedItemsByCategory[category.id as number])?.name ||
                                    category.items?.[0]?.name ||
                                    "Select";
                                return (
                                    <div
                                        key={category.id}
                                        className="flex flex-col gap-[4px] w-full sm:flex-1 relative"
                                        style={{ zIndex: openDropdown === category.id ? 30 : 20 }}
                                    >
                                        <p
                                            className="font-semibold text-[14px] leading-[1.4] tracking-[0.14px] whitespace-nowrap"
                                            style={{ color: labelColor, fontFamily: FONT_VISUAL }}
                                        >
                                            {category.name}:
                                        </p>
                                        <div className="relative w-full">
                                            <button
                                                onClick={() => onDropdownToggle(openDropdown === category.id ? null : (category.id as number))}
                                                className="w-full flex items-center justify-between gap-[10px] p-[10px] rounded-[10px] text-[13px] sm:text-[14px] leading-[1.5] transition-all"
                                                style={{ backgroundColor: DROPDOWN_BG, border: `0.6px solid ${DROPDOWN_BORDER}`, color: INK, fontFamily: FONT_VISUAL }}
                                            >
                                                <span className="flex-1 text-left whitespace-nowrap overflow-hidden text-ellipsis">{selectedName}</span>
                                                <ArrowDownIcon className={cn("shrink-0 size-[20px] transition-transform", openDropdown === category.id && "rotate-180")} />
                                            </button>

                                            {openDropdown === category.id && category.items && category.items.length > 0 && (
                                                <div
                                                    className="absolute z-50 w-full mt-[4px] overflow-hidden rounded-[10px]"
                                                    style={{ backgroundColor: "#FFFFFF", border: `0.6px solid ${DROPDOWN_BORDER}`, boxShadow: "0 8px 24px rgba(0,0,0,0.18)" }}
                                                >
                                                    <div className="max-h-[200px] overflow-y-auto">
                                                        {category.items.map((item: any) => (
                                                            <button
                                                                key={item.id}
                                                                data-cta-name="category_select"
                                                                onClick={() => {
                                                                    onCategoryItemSelect(category.id as number, item.id as number);
                                                                    onDropdownToggle(null);
                                                                }}
                                                                className={cn(
                                                                    "w-full text-left p-[10px] text-[13px] sm:text-[14px] leading-[1.4] tracking-[0.14px] transition-all hover:bg-black/5",
                                                                    selectedItemsByCategory[category.id as number] === item.id && "bg-black/10"
                                                                )}
                                                                style={{ color: themed(item.text_color, ["#FFFFFF"], INK), fontFamily: FONT_VISUAL }}
                                                            >
                                                                {item.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Subjects table */}
                <div className="flex flex-col gap-[2px] w-full min-w-0">
                    {/* Below `sm` the viewport is narrower than this table's readable
                        width, so scroll it horizontally instead of letting the columns
                        squeeze until their text spills into the neighbouring cell. */}
                    <div className="w-full min-w-0 overflow-x-auto">
                        <div className={cn("flex flex-col gap-[2px]", TABLE_MIN_W)}>
                            {/* Header */}
                            <div
                                className={cn("flex items-center justify-center w-full border-t-[0.6px] border-b-[0.6px] border-white", CELL)}
                                style={{ backgroundColor: headerBg }}
                            >
                                <p
                                    className={cn("shrink-0 font-semibold xl:font-medium leading-[1.5] xl:leading-[1.4]", "text-[14px] sm:text-[16px] lg:text-[18px] xl:text-[20px]", TYPE_W)}
                                    style={{ color: parsedData.table_heading_1_color || "#FFFFFF", fontFamily: FONT_POPPINS }}
                                >
                                    {parsedData.table_heading_1}
                                </p>
                                <Divider color={HEADER_DIVIDER} />
                                <p
                                    className={cn("flex-1 min-w-0 font-semibold xl:font-medium leading-[1.5] xl:leading-[1.4]", "text-[14px] sm:text-[16px] lg:text-[18px] xl:text-[20px]")}
                                    style={{ color: parsedData.table_heading_2_color || "#FFFFFF", fontFamily: FONT_POPPINS }}
                                >
                                    {parsedData.table_heading_2}
                                </p>
                                <Divider color={HEADER_DIVIDER} />
                                <p
                                    className={cn("shrink-0 text-center font-semibold xl:font-medium leading-[1.5] xl:leading-[1.4]", "text-[14px] sm:text-[16px] lg:text-[18px] xl:text-[20px]", CREDITS_W)}
                                    style={{ color: parsedData.table_heading_3_color || "#FFFFFF", fontFamily: FONT_POPPINS }}
                                >
                                    {parsedData.table_heading_3}
                                </p>
                            </div>

                            {/* Rows */}
                            {activeSubjects.map((subject, index) => {
                                const isElective = subject.is_elective === 1;
                                const rowBg = subject.background_color || ROW_DEFAULT_BG;
                                const rowText = themed(subject.text_color, ["#FFFFFF"], INK);
                                return (
                                    <div
                                        key={subject.id || index}
                                        className={cn("flex justify-center w-full", CELL, isElective ? "items-start" : "items-center")}
                                        style={{ backgroundColor: rowBg }}
                                    >
                                        <p
                                            className={cn("shrink-0 font-normal", ROW_TEXT, isElective && "text-[16px] sm:text-[16px] leading-normal", TYPE_W)}
                                            style={{ color: rowText, fontFamily: isElective ? FONT_VISUAL : FONT_POPPINS }}
                                        >
                                            {subject.type || "-"}
                                        </p>
                                        <Divider color={ROW_DIVIDER} />

                                        {isElective && (
                                            <button
                                                type="button"
                                                data-cta-name="elective_expand"
                                                title="Expand electives"
                                                onClick={() => onElectiveToggle(expandedElective === subject.id ? null : (subject.id as number))}
                                                className="shrink-0 self-start mt-[1px]"
                                                style={{ color: rowText }}
                                            >
                                                <ArrowDownIcon
                                                    className={cn("size-[14px] sm:size-[20px] transition-transform", expandedElective === subject.id ? "" : "-rotate-90")}
                                                />
                                            </button>
                                        )}

                                        <div className={cn("flex-1 min-w-0 flex flex-col", isElective && "gap-[12px]")}>
                                            <p className={cn("font-normal", ROW_TEXT)} style={{ color: rowText, fontFamily: FONT_POPPINS }}>
                                                {subject.name}
                                            </p>
                                            {isElective && expandedElective === subject.id && subject.electives && subject.electives.length > 0 && (
                                                <ol
                                                    className={cn(
                                                        "list-decimal flex flex-col gap-[12px]",
                                                        "ml-[18px] sm:ml-[21px] xl:ml-[24px]",
                                                        "text-[12px] sm:text-[14px] lg:text-[15px] xl:text-[16px]"
                                                    )}
                                                    style={{ fontFamily: FONT_POPPINS }}
                                                >
                                                    {subject.electives.map((elective: any, idx: number) => (
                                                        <li key={idx} style={{ color: themed(elective.text_color, ["#FFFFFF"], INK), lineHeight: "1.55" }}>
                                                            {elective.name}
                                                        </li>
                                                    ))}
                                                </ol>
                                            )}
                                        </div>

                                        <Divider color={ROW_DIVIDER} />
                                        <p
                                            className={cn("shrink-0 text-center font-normal", ROW_TEXT, CREDITS_W)}
                                            style={{ color: rowText, fontFamily: FONT_POPPINS }}
                                        >
                                            {subject.credits || ""}
                                        </p>
                                    </div>
                                );
                            })}

                            {/* Total */}
                            <div className={cn("flex items-center justify-center w-full border-t-[0.6px] border-b-[0.6px] border-black", CELL)}>
                                <p className={cn("flex-1 min-w-0 font-normal", ROW_TEXT)} style={{ color: totalTextColor, fontFamily: FONT_POPPINS }}>
                                    {totalCreditsText}
                                </p>
                                <Divider color={ROW_DIVIDER} />
                                <p className={cn("shrink-0 text-center font-normal", ROW_TEXT, CREDITS_W)} style={{ color: totalTextColor, fontFamily: FONT_POPPINS }}>
                                    {totalCredits}
                                </p>
                            </div>
                        </div>
                    </div>

                    {parsedData.note && (
                        <p
                            className={cn("italic font-medium leading-[1.5] mt-[8px]", "text-[12px] lg:text-[13px] xl:text-[14px]")}
                            style={{ color: noteColor, fontFamily: FONT_POPPINS }}
                        >
                            {parsedData.note}
                        </p>
                    )}
                </div>

                {/* Tools & Skills */}
                <div className="flex flex-col gap-[24px] w-full sm:flex-row sm:gap-[20px] lg:gap-[28px] sm:items-stretch">
                    {/* Tools */}
                    <div className="flex flex-col gap-[14px] w-full sm:flex-1">
                        <p
                            className="font-semibold leading-[1.5] text-[14px] sm:text-[16px] lg:text-[18px] xl:text-[20px]"
                            style={{ color: toolsTitleColor, fontFamily: FONT_POPPINS }}
                        >
                            {parsedData.tools_title}
                        </p>
                        <div className="flex flex-wrap gap-[12px] content-start">
                            {parsedData.tools?.map((tool, index) => {
                                const customBg = tool.background_color && tool.background_color !== "#1A1A1A" ? tool.background_color : null;
                                return (
                                    <div
                                        key={index}
                                        className="flex flex-col items-start px-[12px] py-[8px] rounded-[10px] sm:px-[18px] sm:py-[12px] sm:rounded-[20px] lg:px-[20px] lg:py-[13px]"
                                        style={customBg ? { backgroundColor: customBg } : { backgroundImage: TOOL_PILL_GRADIENT }}
                                    >
                                        {tool.image && (
                                            <div className="relative shrink-0 w-[60px] h-[24px] sm:w-[85px] sm:h-[34px] lg:w-[95px] lg:h-[38px]">
                                                <Image src={tool.image} alt={tool.alt_image || "Tool"} fill className="object-contain" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Vertical divider (desktop only) */}
                    <div className="hidden sm:block w-px self-stretch shrink-0" style={{ backgroundColor: ROW_DIVIDER }} />

                    {/* Skills */}
                    <div className="flex flex-col gap-[14px] w-full sm:flex-1">
                        <p
                            className="font-semibold leading-[1.5] text-[14px] sm:text-[16px] lg:text-[18px] xl:text-[20px]"
                            style={{ color: skillsTitleColor, fontFamily: FONT_POPPINS }}
                        >
                            {parsedData.skills_title}
                        </p>
                        <div className="flex flex-wrap gap-[13px] content-start">
                            {parsedData.skills?.map((skill, index) => {
                                const pillBg = themed(skill.background_color, ["#1A1A1A"], SKILL_PILL_BG);
                                const skillText = themed(skill.text_color, ["#FFFFFF"], INK);
                                return (
                                    <div key={index} className="flex items-center justify-center px-[10px] py-[8px] rounded-[20px]" style={{ backgroundColor: pillBg }}>
                                        <span
                                            className="flex items-center gap-[4px] font-semibold leading-[1.49] whitespace-nowrap text-[12px] sm:text-[14px] xl:text-[16px]"
                                            style={{ color: skillText, fontFamily: FONT_VISUAL }}
                                        >
                                            {skill.icon ? (
                                                <span className="relative inline-block shrink-0 w-[12px] h-[12px] sm:w-[14px] sm:h-[14px]">
                                                    <Image src={skill.icon} alt={skill.alt_icon || skill.name || "Skill"} fill className="object-contain" />
                                                </span>
                                            ) : (
                                                <span>✓</span>
                                            )}
                                            {skill.name}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* CTA button */}
                {parsedData.cta_button_text && (
                    <div className="w-full flex justify-center">
                        <button
                            data-cta-name="download_brochure"
                            onClick={onCtaClick}
                            className="flex items-center justify-center gap-[10px] px-[24px] py-[16px] rounded-[8px] transition-transform w-full sm:w-auto"
                            style={{
                                backgroundColor: ctaBg,
                                color: ctaTextColor,
                                fontFamily: FONT_POPPINS,
                                ...(shadowEnabled
                                    ? { boxShadow: `${CTA_SHADOW_OFFSET} ${shadowColor}` }
                                    : {}),
                            }}
                        >
                            <span className="font-semibold leading-[1.4] whitespace-nowrap text-[16px] sm:text-[14px] lg:text-[16px]">
                                {parsedData.cta_button_text}
                            </span>
                            {parsedData.cta_button_icon ? (
                                <span className="relative inline-block shrink-0 size-[24px] sm:size-[20px] lg:size-[24px]">
                                    <Image src={parsedData.cta_button_icon} alt={parsedData.cta_button_icon_alt || "Download"} fill className="object-contain" />
                                </span>
                            ) : (
                                <ArrowDownIcon className="size-[24px] sm:size-[20px] lg:size-[24px]" />
                            )}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};
